import React, { useState, useEffect } from "react";
import useCmsStore from "../../../services/useCmsStore";
import { toast } from "react-toastify";
import { FiX, FiUser, FiSave, FiGlobe, FiTwitter, FiLinkedin, FiInstagram } from "react-icons/fi";
import "./Modal.css";

const AuthorModal = ({ isOpen, onClose, author = null }) => {
  const { createAuthor, updateAuthor } = useCmsStore();
  const isEditing = !!author;

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    email: "",
    bio: "",
    short_bio: "",
    job_title: "",
    website: "",
    social_links: {
      twitter: "",
      linkedin: "",
      instagram: "",
    },
    seo_title: "",
    seo_description: "",
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (author) {
      setFormData({
        name: author.name || "",
        slug: author.slug || "",
        email: author.email || "",
        bio: author.bio || "",
        short_bio: author.short_bio || "",
        job_title: author.job_title || "",
        website: author.website || "",
        social_links: {
          twitter: author.social_links?.twitter || "",
          linkedin: author.social_links?.linkedin || "",
          instagram: author.social_links?.instagram || "",
        },
        seo_title: author.seo_title || "",
        seo_description: author.seo_description || "",
      });
    } else {
      setFormData({
        name: "",
        slug: "",
        email: "",
        bio: "",
        short_bio: "",
        job_title: "",
        website: "",
        social_links: {
          twitter: "",
          linkedin: "",
          instagram: "",
        },
        seo_title: "",
        seo_description: "",
      });
    }
  }, [author, isOpen]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "name" && !isEditing) {
        newData.slug = generateSlug(value);
      }
      return newData;
    });
  };

  const handleSocialChange = (platform, value) => {
    setFormData((prev) => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        await updateAuthor(author.id, formData);
        toast.success("Author updated successfully");
      } else {
        await createAuthor(formData);
        toast.success("Author created successfully");
      }
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save author");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cms-modal-overlay" onClick={onClose}>
      <div className="cms-modal cms-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="cms-modal-header">
          <div className="cms-modal-title">
            <FiUser size={20} />
            <h3>{isEditing ? "Edit Author" : "New Author"}</h3>
          </div>
          <button className="cms-modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="cms-modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Full name"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  placeholder="author-slug"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="author@example.com"
                />
              </div>
              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => handleChange("job_title", e.target.value)}
                  placeholder="e.g., Content Writer"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Short Bio (255 chars)</label>
              <input
                type="text"
                value={formData.short_bio}
                onChange={(e) => handleChange("short_bio", e.target.value)}
                placeholder="Brief introduction..."
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label>Full Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleChange("bio", e.target.value)}
                placeholder="Detailed biography..."
                rows={4}
              />
            </div>

            <div className="form-divider">
              <span>Social Links</span>
            </div>

            <div className="form-group">
              <label>
                <FiGlobe size={14} style={{ marginRight: 6 }} />
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleChange("website", e.target.value)}
                placeholder="https://yourwebsite.com"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <FiTwitter size={14} style={{ marginRight: 6 }} />
                  Twitter
                </label>
                <input
                  type="text"
                  value={formData.social_links.twitter}
                  onChange={(e) => handleSocialChange("twitter", e.target.value)}
                  placeholder="@username"
                />
              </div>
              <div className="form-group">
                <label>
                  <FiLinkedin size={14} style={{ marginRight: 6 }} />
                  LinkedIn
                </label>
                <input
                  type="text"
                  value={formData.social_links.linkedin}
                  onChange={(e) => handleSocialChange("linkedin", e.target.value)}
                  placeholder="Profile URL or username"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <FiInstagram size={14} style={{ marginRight: 6 }} />
                Instagram
              </label>
              <input
                type="text"
                value={formData.social_links.instagram}
                onChange={(e) => handleSocialChange("instagram", e.target.value)}
                placeholder="@username"
              />
            </div>

            <div className="form-divider">
              <span>SEO Settings</span>
            </div>

            <div className="form-group">
              <label>SEO Title</label>
              <input
                type="text"
                value={formData.seo_title}
                onChange={(e) => handleChange("seo_title", e.target.value)}
                placeholder="SEO title for author page"
                maxLength={70}
              />
            </div>

            <div className="form-group">
              <label>SEO Description</label>
              <textarea
                value={formData.seo_description}
                onChange={(e) => handleChange("seo_description", e.target.value)}
                placeholder="Meta description for author page..."
                rows={2}
                maxLength={160}
              />
            </div>
          </div>

          <div className="cms-modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              <FiSave size={16} />
              <span>{isSaving ? "Saving..." : isEditing ? "Update" : "Create"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthorModal;
